<template>
  <div class="ask-main">
    <div
      ref="scrollContainer"
      class="ask-main-scroller"
    >
      <div
        ref="contentWrapper"
        class="ask-main-content"
      >
        <AskIntro />
        <AskMessages />
      </div>

      <div
        ref="bottomAnchor"
        style="height: 1px;"
      />
    </div>

    <div class="ask-main-form">
      <div class="ask-controls">
        <Badge
          class="ask-loader"
          :class="{'is-active': chatStore.toolActivityCount > 0}"
        >
          <Loader />
          {{ chatStore.lastToolDescription || 'Working' }}…
        </Badge>

        <div
          class="ask-scroll-to-bottom"
          :class="{ 'is-active': !shouldAutoScroll }"
        >
          <Badge
            icon-before="fas fa-arrow-down"
            size="3"
            clickable
            status="ask-anything"
            @click="scrollToBottom(true)"
          >
            New messages
          </Badge>
        </div>
      </div>

      <AskChatForm />

      <p class="ask-disclaimer">
        The AI Assistant may make mistakes. We recommend checking important information.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useChatStore } from '@frontend/ui/stores/chatStore';
import { Badge, Loader } from '@stakeui';

import AskIntro from './AskIntro.vue';
import AskChatForm from './AskChatForm.vue';
import AskMessages from './AskMessages.vue';

const chatStore = useChatStore();

const scrollContainer = ref(null);
const bottomAnchor = ref(null);
const contentWrapper = ref(null);
const shouldAutoScroll = ref(true); // true while user is at/near bottom

// const isNearBottom = (el, threshold = 16) =>
//   el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;

const scrollToBottom = (smooth = false) => {
  const anchor = bottomAnchor.value;
  if (!anchor) return;
  anchor.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
};

// const handleUserScroll = () => {
//   const el = scrollContainer.value;
//   if (!el) return;
//   shouldAutoScroll.value = isNearBottom(el);
// };

let intersectionObserver;
let resizeObserver;

onMounted(async () => {
  await nextTick();
  scrollToBottom(false);

  intersectionObserver = new IntersectionObserver(
      entries => {
        shouldAutoScroll.value = entries.some(e => e.isIntersecting);
      },
      { root: scrollContainer.value, threshold: 1.0 },
  );
  if (bottomAnchor.value) intersectionObserver.observe(bottomAnchor.value);

  // still keep ResizeObserver for content growth
  resizeObserver = new ResizeObserver(() => {
    if (shouldAutoScroll.value) {
      requestAnimationFrame(() => scrollToBottom(false));
    }
  });
  resizeObserver.observe(contentWrapper.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  intersectionObserver?.disconnect();
});
</script>

<style scoped>
.ask-main {
  --offset: 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.ask-main-scroller {
  overflow-x: hidden;
  overflow-y: scroll;
  flex-grow: 1;
  position: relative;
}

.ask-main-content {
  padding: var(--offset);
  padding-bottom: calc(var(--offset) + 0.5rem);
}

.ask-main-form {
  flex-shrink: 0;
  padding: 0 var(--offset);
  position: relative;
}

.ask-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem var(--offset);
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  /* background: hsl(0deg 0% 100% / 80%); */
  /* backdrop-filter: blur(1px); */
  /* box-shadow: 0 0px 8px 8px white; */
}

.ask-loader,
.ask-scroll-to-bottom {
  opacity: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;

  &.is-active {
    opacity: 1;
  }
}

.ask-loader {
  background: hsl(var(--stk-text-h) var(--stk-text-s) 94% / 80%);
  backdrop-filter: blur(1px);
}

.ask-disclaimer {
  font-size: 0.75em;
  padding: 1em;
  text-align: center;
  color: var(--stk-text-weak);
  text-align: center;
}
</style>
